import { Box, Flex, Text, Grid, chakra } from '@chakra-ui/react';
import { EventCard } from '../EventCard/EventCard';
import type { Organization } from '@/types/events';

interface YourEventsSectionProps {
  events: Organization[];
  loading: boolean;
  subscriptionActive: boolean;
  onEventPress: (event: Organization) => void;
  onEditEvent: (event: Organization, e: React.MouseEvent) => void;
  onManageEvent?: (event: Organization, e: React.MouseEvent) => void;
  onCreateClick: () => void;
}

export function YourEventsSection({
  events,
  loading,
  subscriptionActive,
  onEventPress,
  onEditEvent,
  onManageEvent,
  onCreateClick,
}: YourEventsSectionProps) {
  const activeEvents = events.filter((e) => {
    if (e.is_active === false) return false;
    if (e.status === 'Ended') return false;
    if (e.end_date) {
      const endDate = new Date(e.end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      if (endDate < today) return false;
    }
    return true;
  });

  const activeCount = activeEvents.length;
  const canCreate = activeCount < 3 && subscriptionActive;

  return (
    <Box as="section" mb="8" animation="fadeIn 0.3s ease-in-out">
      <Flex align="center" justify="space-between" mb="4" flexWrap="nowrap" gap="3">
        <Flex align="center" gap="2">
          <Text as="h2" m="0" fontSize={{ base: 'md', sm: 'lg' }} fontWeight="semibold" color="volyx.text" fontFamily="body">
            Your Events
          </Text>
          <Text as="span" fontSize={{ base: 'xs', sm: 'sm' }} color="volyx.textSecondary" fontFamily="body">
            {activeCount}/3 active
          </Text>
        </Flex>
        <chakra.button
          flexShrink={0}
          whiteSpace="nowrap"
          px={{ base: '0', sm: '6' }}
          py={{ base: '0', sm: '3' }}
          h={{ base: '9', sm: '12' }}
          w={{ base: '9', sm: 'auto' }}
          minW={{ base: '9', sm: 'auto' }}
          maxW={{ base: '9', sm: 'none' }}
          borderRadius={{ base: '16px', sm: '20px' }}
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          outline="none"
          bg="volyx.lightGray"
          color="volyx.text"
          fontSize={{ base: 'xl', sm: 'md' }}
          lineHeight={{ base: 'none', sm: 'normal' }}
          _hover={{ bg: 'volyx.mediumGray', transform: 'scale(1.01)' }}
          onClick={onCreateClick}
          disabled={!canCreate}
        >
          <Box as="span" display={{ base: 'none', sm: 'inline' }}>Create </Box>+
        </chakra.button>
      </Flex>
      <Box minH="120px">
        {loading ? (
          <Text m="0" fontSize="sm" color="volyx.textSecondary" fontFamily="body" py="6">
            Loading your events...
          </Text>
        ) : activeEvents.length === 0 ? (
          <Box py="8" px="4" textAlign="center" bg="volyx.lightGray" borderRadius="20px">
            <Text m="0" mb="1" fontSize="md" fontWeight="semibold" color="volyx.text" fontFamily="body">
              No Active Events
            </Text>
            <Text m="0" fontSize="sm" color="volyx.textSecondary" fontFamily="body">
              All your events have ended
            </Text>
          </Box>
        ) : (
          <Grid
            templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
            gap="5"
            w="full"
            justifyItems={{ base: 'stretch', sm: 'center', lg: 'stretch' }}
            maxW={{ base: 'none', sm: '680px', lg: 'none' }}
            mx={{ base: undefined, sm: 'auto', lg: undefined }}
          >
            {activeEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onPress={() => onEventPress(event)}
                showEditButton
                onEdit={(e) => onEditEvent(event, e)}
                onManage={onManageEvent ? (e) => onManageEvent(event, e) : undefined}
                subscriptionBlocked={!subscriptionActive}
              />
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
